import { useState } from 'react'
import type { CSSProperties } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Star } from 'lucide-react'
import { useReducedMotion } from 'framer-motion'
import { api } from '@/lib/api'
import { cn } from '@/lib/cn'
import type { Testimonial } from '@/types'
import Reveal from '@/components/storefront/Reveal'
import SectionHeading from '@/components/storefront/SectionHeading'

/**
 * Customer quotes, managed in admin > Testimonials.
 *
 * Unlike BrandStrip there is no hardcoded fallback. The quotes the site
 * launched with now live in the database, and a failed request must not bring
 * back ones the admin has since deleted, so loading, failure and an empty list
 * all render nothing.
 */

/** Below this the strip sits still: a ticker of one or two cards shows the same face over and over. */
const MIN_MARQUEE = 3
/** Cards in each loop copy. A copy narrower than the window slides a gap into view; 8 cards cover 2560px. */
const MIN_PER_COPY = 8
/** Held per card, so a longer list scrolls at the same speed instead of faster. 45s over the launch six. */
const SECONDS_PER_CARD = 7.5

type CardContent = Pick<Testimonial, 'name' | 'role' | 'quote' | 'rating' | 'image_path'>

export function Stars({ rating }: { rating: number }) {
  const filled = Math.max(0, Math.min(5, rating))
  return (
    <span className="flex items-center gap-0.5" aria-label={`${filled} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={i}
          aria-hidden
          className={i < filled ? 'size-4 fill-amber-400 text-amber-400' : 'size-4 text-foreground/20'}
        />
      ))}
    </span>
  )
}

function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}

/**
 * Photo, or the person's initials when there is none or it fails to load.
 * Decorative either way - the name sits right beside it. Callers key it by
 * `src` so a replaced photo gets a fresh attempt after an earlier failure.
 */
export function TestimonialAvatar({ name, src }: { name: string; src: string | null }) {
  const [failed, setFailed] = useState(false)

  if (src && !failed) {
    return (
      <img src={src} alt="" onError={() => setFailed(true)} className="size-11 shrink-0 rounded-full object-cover" />
    )
  }

  return (
    <span
      aria-hidden
      className="grid size-11 shrink-0 place-items-center rounded-full bg-foreground/5 text-sm font-semibold text-foreground/70"
    >
      {initials(name)}
    </span>
  )
}

/**
 * One card. Carries no outer margin of its own: the marquee adds `mr-4`
 * rather than a track gap, which keeps the two halves evenly spaced so the
 * -50% loop point is invisible. `fluid` lets it shrink inside a narrow box
 * (the admin preview) instead of holding the strip's fixed width.
 */
export function TestimonialCard({
  t,
  hidden = false,
  fluid = false,
  className,
}: {
  t: CardContent
  hidden?: boolean
  fluid?: boolean
  className?: string
}) {
  return (
    <div
      aria-hidden={hidden || undefined}
      className={cn(
        'rounded-xl border border-foreground/10 bg-background p-5',
        fluid ? 'w-full max-w-[360px]' : 'w-[320px] shrink-0 sm:w-[360px]',
        className,
      )}
    >
      <div className="flex items-center gap-3">
        <TestimonialAvatar key={t.image_path ?? ''} name={t.name} src={t.image_path} />
        <div className="min-w-0">
          <p className="text-sm font-bold break-words">{t.name}</p>
          <Stars rating={t.rating} />
        </div>
      </div>
      {t.role && <p className="mt-1.5 text-xs text-foreground/50">{t.role}</p>}
      <p className="mt-3 text-sm leading-relaxed break-words text-foreground/80">{t.quote}</p>
    </div>
  )
}

function Marquee({ rows }: { rows: Testimonial[] }) {
  const copy = Array.from({ length: Math.ceil(MIN_PER_COPY / rows.length) }, () => rows).flat()
  const style: CSSProperties = { animationDuration: `${copy.length * SECONDS_PER_CARD}s` }

  return (
    <div className="group mt-8 w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]">
      <div
        className="flex w-max animate-[marquee_45s_linear_infinite] group-hover:[animation-play-state:paused]"
        style={style}
      >
        {/* Only the first pass through the list is read out; the repeats are filler. */}
        {[...copy, ...copy].map((t, i) => (
          <TestimonialCard key={i} t={t} hidden={i >= rows.length} className="mr-4" />
        ))}
      </div>
    </div>
  )
}

export default function Testimonials() {
  const reduce = useReducedMotion()
  const testimonials = useQuery({
    queryKey: ['storefront-testimonials'],
    queryFn: async () => (await api.get<{ data: Testimonial[] }>('/storefront/testimonials')).data.data,
  })

  const rows = testimonials.data ?? []
  if (rows.length === 0) return null

  return (
    <section className="py-12">
      <Reveal>
        <div className="mx-auto w-full max-w-6xl px-5">
          <SectionHeading
            align="center"
            title="What our customers say"
            subtitle="Teams, schools and businesses across Perak print with us - here is how it went for them"
          />
        </div>

        {rows.length < MIN_MARQUEE ? (
          <div className="mx-auto mt-8 flex w-full max-w-6xl flex-wrap justify-center gap-4 px-5">
            {rows.map((t) => (
              <TestimonialCard key={t.id} t={t} />
            ))}
          </div>
        ) : reduce ? (
          // Reduced motion: same strip, user-driven scroll instead of a ticker.
          <div className="scrollbar-thin mt-8 flex w-full overflow-x-auto px-5 pb-2">
            {rows.map((t) => (
              <TestimonialCard key={t.id} t={t} className="mr-4" />
            ))}
          </div>
        ) : (
          <Marquee rows={rows} />
        )}
      </Reveal>
    </section>
  )
}
